<script setup lang="ts">

import Button from 'primevue/button';
import Column from 'primevue/column';
import ConfirmDialog from 'primevue/confirmdialog';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import ExclamationTriangleIcon from '@primeicons/vue/exclamation-triangle';
import InfoCircleIcon from '@primeicons/vue/info-circle';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import Modal from '@apparatix/Components/Modal.vue';
import ProgressSpinner from 'primevue/progressspinner';
import Select from 'primevue/select';
import SelectButton from 'primevue/selectbutton';
import { computed, onUnmounted, ref, watch } from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import { useEndpoint, useToastNotifications } from '@apparatix/composables';
import { pricingDescriptions } from '@apparatix/stores/usePricingStore';
import type { PricingComponentOption, PricingTableFilters, SalesContextCode } from '@apparatix/stores/usePricingStore';
import { CONTEXT_STYLES, formatCurrency, toDateString } from '@apparatix/utilities/pricingFormatting';

type SelectionDescriptor = { mode: 'ids'; ids: number[] } | { mode: 'allMatchingFilter' };

type Props = {
  visible: boolean;
  context: SalesContextCode;
  componentOptions: PricingComponentOption[];
  canViewFloor: boolean;
  filters: PricingTableFilters;
  selection: SelectionDescriptor;
};

type Emits = {
  (e: 'close'): void;
  (e: 'completed'): void;
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const confirm = useConfirm();
const toast = useToastNotifications();

type FieldOption = { value: string; label: string; key: string; role: 'standard' | 'floor' };

const fieldOptions = computed<FieldOption[]>(() => props.componentOptions.flatMap(component => [
  {
    value: `${component.key}:standard`, label: component.label, key: component.key, role: 'standard' as const,
  },
  ...(component.hasFloor && props.canViewFloor
    ? [{
      value: `${component.key}:floor`, label: `${component.label} Floor`, key: component.key, role: 'floor' as const,
    }]
    : []),
]));

const TARGET_CONTEXT_OPTIONS: { value: SalesContextCode; label: string }[] = [
  { value: 'local', label: CONTEXT_STYLES.local.label },
  { value: 'national', label: CONTEXT_STYLES.national.label },
];

// "Set to" only makes sense against a literal typed amount — it ignores whatever basis is
// selected, so it's hidden once the user picks "another field" to avoid a combination that looks
// like it should use the source field but silently doesn't.
const OPERATION_LABELS: Record<'self' | 'source', Record<'set' | 'add' | 'subtract' | 'multiply', string>> = {
  self: {
    set: 'Set to', add: 'Add to existing', subtract: 'Subtract from existing', multiply: 'Multiply existing by',
  },
  source: {
    set: 'Set to', add: 'Add to source field', subtract: 'Subtract from source field', multiply: 'Multiply source field by',
  },
};

const ROUNDING_OPTIONS = [
  { value: 'none', label: 'No rounding' },
  { value: '0.5', label: '$0.50' },
  { value: '1', label: '$1' },
  { value: '5', label: '$5' },
  { value: '10', label: '$10' },
  { value: 'custom', label: 'Custom…' },
];

const ROUNDING_DIRECTION_OPTIONS: { value: 'nearest' | 'up'; label: string }[] = [
  { value: 'nearest', label: 'Nearest' },
  { value: 'up', label: 'Up' },
];

const BASIS_OPTIONS: { value: 'self' | 'source'; label: string }[] = [
  { value: 'self', label: "This field's current value" },
  { value: 'source', label: 'Another field\'s value' },
];

const TIMING_OPTIONS: { value: 'now' | 'future'; label: string }[] = [
  { value: 'now', label: 'Apply immediately' },
  { value: 'future', label: 'Schedule for a future date' },
];

const timing = ref<'now' | 'future'>('now');
const effectiveDate = ref<Date | null>(null);
const tomorrow = computed(() => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date;
});

const targetContext = ref<SalesContextCode>(props.context);
const selectedFieldValue = ref<string | null>(null);
const selectedField = computed(() => fieldOptions.value.find(option => option.value === selectedFieldValue.value) ?? null);

const basis = ref<'self' | 'source'>('self');
const sourceContext = ref<SalesContextCode>(props.context);
const sourceContextLabel = computed(() => TARGET_CONTEXT_OPTIONS.find(option => option.value === sourceContext.value)?.label ?? sourceContext.value);
const sourceFieldValue = ref<string | null>(null);
const sourceField = computed(() => fieldOptions.value.find(option => option.value === sourceFieldValue.value) ?? null);

const operationOptions = computed(() => (['set', 'add', 'subtract', 'multiply'] as const)
  .filter(value => basis.value === 'self' || value !== 'set')
  .map(value => ({ value, label: OPERATION_LABELS[basis.value][value] })));

const operation = ref<'set' | 'add' | 'subtract' | 'multiply'>('set');

// "Set to" disappears from operationOptions once basis switches to "source" — move off it so the
// Select isn't left pointing at a hidden option.
watch(basis, (value) => {
  if (value === 'source' && operation.value === 'set') {
    operation.value = 'multiply';
  }
});

const operand = ref<number | null>(null);

// A plain text field for the multiply Factor, decoupled from `operand` — PrimeVue's InputNumber
// mode="decimal" cannot parse a bare leading "." typed into an empty field (it formats it to
// "NaN" and drops it), so ".3" never becomes 0.3. Kept as its own ref rather than a computed
// wrapping `operand` because deriving the displayed text from the parsed number on every
// keystroke would strip the "." the user just typed before they can finish "0.3".
const factorText = ref('');

// Plain decimal only — Number() also accepts scientific notation ("1e5") and hex ("0x5"), which
// would silently turn a typo into a wildly different multiplier.
const PLAIN_DECIMAL = /^-?\d*\.?\d*$/;

watch(factorText, (value) => {
  const trimmed = value.trim();

  if (trimmed === '' || trimmed === '-' || trimmed === '.' || trimmed === '-.' || !PLAIN_DECIMAL.test(trimmed)) {
    operand.value = null;

    return;
  }

  const parsed = Number(trimmed);
  operand.value = Number.isNaN(parsed) ? null : parsed;
});

// Keeps the Factor text in sync if `operand` was set some other way (e.g. reset on reopen) while
// switching into multiply — the InputNumber used for the other operations writes to `operand`
// directly and never touches `factorText`.
watch(operation, (value) => {
  if (value === 'multiply') {
    factorText.value = operand.value === null ? '' : String(operand.value);
  }
});

const roundingChoice = ref('none');
const customRounding = ref<number | null>(null);
const roundingIncrement = computed(() => {
  if (roundingChoice.value === 'none') {
    return null;
  }

  return roundingChoice.value === 'custom' ? customRounding.value : Number(roundingChoice.value);
});
const roundingDirection = ref<'nearest' | 'up'>('nearest');

const formatAmount = (value: string | number | null | undefined) => formatCurrency(value, '—');

const selectionCount = computed(() => (props.selection.mode === 'ids' ? props.selection.ids.length : null));

type PreviewRow = {
  inv_id: number;
  inv_number: string;
  currentAmount: string | null;
  newAmount: string | null;
  skip: boolean;
  skipReason: string | null;
};

type PreviewResponse = { data?: { totalMatched: number; sampleSize: number; rows: PreviewRow[] }};

const totalMatched = ref<number | null>(null);
const sampleSize = ref(0);
const previewRows = ref<PreviewRow[]>([]);
const hasPreviewed = ref(false);

const buildBasePayload = () => ({
  context: targetContext.value,
  componentKey: selectedField.value?.key,
  role: selectedField.value?.role,
  operation: operation.value,
  operand: operand.value,
  roundingIncrement: roundingIncrement.value,
  ...(roundingIncrement.value !== null ? { roundingDirection: roundingDirection.value } : {}),
  ...(timing.value === 'future' ? { effectiveStartDate: toDateString(effectiveDate.value) } : {}),
  ...(basis.value === 'source' && sourceField.value
    ? { source: { componentKey: sourceField.value.key, role: sourceField.value.role, context: sourceContext.value }}
    : {}),
  ...(props.selection.mode === 'allMatchingFilter'
    ? { filters: props.filters, selection: { mode: 'allMatchingFilter' }}
    : { selection: { mode: 'ids', ids: props.selection.ids }}),
});

const { getResults: fetchPreview, isLoading: isPreviewLoading } = useEndpoint<PreviewResponse>({
  type: 'POST',
  url: '/manage-pricing/bulk-edit/preview',
  successField: 'data',
  cancelPreviousRequestOnSubsequent: true,
  toast: { showOnSuccess: false },
});

const canRunPreview = computed(() => selectedField.value !== null
  && operand.value !== null
  && (roundingChoice.value !== 'custom' || customRounding.value !== null)
  && (basis.value === 'self' || sourceField.value !== null)
  && (timing.value === 'now' || effectiveDate.value !== null));

const PREVIEW_DEBOUNCE_MS = 400;
let previewDebounceTimer: ReturnType<typeof setTimeout> | undefined;

const runPreview = async () => {
  if (!canRunPreview.value) {
    hasPreviewed.value = false;
    totalMatched.value = null;
    previewRows.value = [];

    return;
  }

  const response = await fetchPreview(buildBasePayload());

  if (response?.data) {
    totalMatched.value = response.data.totalMatched;
    sampleSize.value = response.data.sampleSize;
    previewRows.value = response.data.rows;
    hasPreviewed.value = true;
  }
};

watch([targetContext, selectedFieldValue, basis, sourceContext, sourceFieldValue, operation, operand, roundingIncrement, roundingDirection, timing, effectiveDate], () => {
  hasPreviewed.value = false;

  if (previewDebounceTimer) {
    clearTimeout(previewDebounceTimer);
  }

  previewDebounceTimer = setTimeout(runPreview, PREVIEW_DEBOUNCE_MS);
});

type ApplyResponse = { data?: { batchId: number; status: string }};
type StatusResponse = {
  data?: {
    status: 'queued' | 'processing' | 'completed' | 'failed';
    totalMatched: number | null;
    affectedCount: number;
    skippedCount: number;
    failedCount: number;
    errorSummary: string | null;
  };
};

const applyState = ref<'idle' | 'queued' | 'processing' | 'completed' | 'failed'>('idle');
const applyProgress = ref<{ affectedCount: number; skippedCount: number; failedCount: number } | null>(null);
const applyError = ref<string | null>(null);

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 150;
let pollAttempts = 0;
let pollTimer: ReturnType<typeof setTimeout> | null = null;

const stopPolling = () => {
  if (pollTimer !== null) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};

// Without this, navigating away mid-apply leaves the poll loop firing (up to
// MAX_POLL_ATTEMPTS) against an endpoint this instance no longer displays anything for.
onUnmounted(() => {
  stopPolling();

  if (previewDebounceTimer) {
    clearTimeout(previewDebounceTimer);
  }
});

const { makeRequest: makePollRequest, setEndpointUrl: setPollUrl } = useEndpoint<StatusResponse>({
  type: 'GET',
  successField: 'data',
  toast: { showOnSuccess: false, showOnError: false },
  onSuccess: (response) => {
    const status = response?.data;

    if (!status) {
      return;
    }

    applyProgress.value = {
      affectedCount: status.affectedCount,
      skippedCount: status.skippedCount,
      failedCount: status.failedCount,
    };

    if (status.status === 'completed') {
      applyState.value = 'completed';

      const verbAndWhen = timing.value === 'future' && effectiveDate.value
        ? `scheduled to change effective ${toDateString(effectiveDate.value)}`
        : 'updated';

      toast.showSuccess(
        `${status.affectedCount} record${status.affectedCount === 1 ? '' : 's'} ${verbAndWhen}${status.skippedCount ? `, ${status.skippedCount} skipped` : ''}.`,
        'Bulk edit complete',
      );
      emit('completed');

      return;
    }

    if (status.status === 'failed') {
      applyState.value = 'failed';
      applyError.value = status.errorSummary ?? 'The bulk edit failed.';

      return;
    }

    schedulePoll();
  },
  onError: () => {
    applyState.value = 'failed';
    applyError.value = 'Lost track of the bulk edit\'s progress. Refresh the grid to check its result.';
  },
});

const schedulePoll = () => {
  if (pollAttempts >= MAX_POLL_ATTEMPTS) {
    applyState.value = 'failed';
    applyError.value = 'This is taking longer than expected. Refresh the grid to check its result.';

    return;
  }

  pollAttempts += 1;
  pollTimer = setTimeout(() => makePollRequest(), POLL_INTERVAL_MS);
};

const { makeRequest: makeApplyRequest, isLoading: isApplyLoading } = useEndpoint<ApplyResponse>({
  type: 'POST',
  url: '/manage-pricing/bulk-edit/apply',
  successField: 'data',
  toast: { showOnSuccess: false, showOnError: true, errorMessage: 'Unable to start the bulk edit.' },
  onSuccess: (response) => {
    const batchId = response?.data?.batchId;

    if (!batchId) {
      applyState.value = 'idle';

      return;
    }

    applyState.value = 'queued';
    pollAttempts = 0;
    setPollUrl(`/manage-pricing/bulk-edit/${batchId}`);
    schedulePoll();
  },
  onError: () => {
    applyState.value = 'idle';
  },
});

const doApply = async () => {
  applyState.value = 'queued';
  await makeApplyRequest(buildBasePayload());
};

const confirmApply = () => {
  const whenClause = timing.value === 'future' && effectiveDate.value
    ? ` effective ${toDateString(effectiveDate.value)}`
    : '';

  confirm.require({
    group: 'bulk-edit-apply',
    header: 'Apply bulk edit?',
    message: `This will update ${totalMatched.value ?? 0} record${totalMatched.value === 1 ? '' : 's'}${whenClause} — continue?`,
    icon: ExclamationTriangleIcon,
    acceptLabel: 'Apply',
    rejectLabel: 'Cancel',
    accept: doApply,
  });
};

const isProcessing = computed(() => applyState.value === 'queued' || applyState.value === 'processing');

// The modal is toggled via :visible rather than v-if, so this instance (and its refs) survives
// being closed. Reset whenever it becomes visible again — covers Cancel, the X button, a
// completed run (closed by the parent after emit('completed')), and a failed run's Close button
// alike — so opening it always starts from a clean form rather than the previous run's result
// screen, and the context selects re-sync to wherever the grid is now pointed rather than
// wherever it was the last time this modal opened.
watch(() => props.visible, (visible) => {
  if (!visible) {
    return;
  }

  stopPolling();
  targetContext.value = props.context;
  selectedFieldValue.value = null;
  basis.value = 'self';
  sourceContext.value = props.context;
  sourceFieldValue.value = null;
  operation.value = 'set';
  operand.value = null;
  factorText.value = '';
  roundingChoice.value = 'none';
  customRounding.value = null;
  roundingDirection.value = 'nearest';
  timing.value = 'now';
  effectiveDate.value = null;
  totalMatched.value = null;
  previewRows.value = [];
  hasPreviewed.value = false;
  applyState.value = 'idle';
  applyProgress.value = null;
  applyError.value = null;
});

const resetAndClose = () => emit('close');

</script>

<template>
  <Modal
    :visible="visible"
    title="Bulk Edit Pricing"
    :subtitle="selectionCount !== null ? `${selectionCount} row(s) selected` : 'All rows matching the current filter'"
    disable-footer
    width="w-screen lg:w-[70vw] xl:w-1/2"
    @close="resetAndClose"
  >
    <template #body>
      <div class="flex flex-col gap-4 p-4">
        <div v-if="applyState === 'idle'" class="flex flex-col gap-5">
          <div class="flex flex-col gap-3">
            <h4 class="text-xs font-bold uppercase tracking-wide text-gray-600">
              Target field
            </h4>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium">Sales Context</label>
                <Select
                  v-model="targetContext"
                  :options="TARGET_CONTEXT_OPTIONS"
                  option-label="label"
                  option-value="value"
                  class="w-full"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="flex items-center gap-1 text-sm font-medium">
                  Field
                  <InfoCircleIcon
                    v-if="selectedField?.role === 'floor'"
                    v-tooltip="pricingDescriptions.floorTooltip(selectedField?.key ?? '', selectedField?.label ?? '')"
                    color="var(--tw-text-gray-500)"
                    class="cursor-pointer"
                  />
                </label>
                <Select
                  v-model="selectedFieldValue"
                  :options="fieldOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Select a field"
                  class="w-full"
                />
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-3 border-t border-[var(--secondary-border-color)] pt-4">
            <h4 class="text-xs font-bold uppercase tracking-wide text-gray-600">
              Base value
            </h4>
            <SelectButton
              v-model="basis"
              :options="BASIS_OPTIONS"
              option-label="label"
              option-value="value"
              :allow-empty="false"
            />
            <div v-if="basis === 'source'" class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium">Source Sales Context</label>
                <Select
                  v-model="sourceContext"
                  :options="TARGET_CONTEXT_OPTIONS"
                  option-label="label"
                  option-value="value"
                  class="w-full"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium">Source Field</label>
                <Select
                  v-model="sourceFieldValue"
                  :options="fieldOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Select a field"
                  class="w-full"
                />
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-3 border-t border-[var(--secondary-border-color)] pt-4">
            <h4 class="text-xs font-bold uppercase tracking-wide text-gray-600">
              When
            </h4>
            <SelectButton
              v-model="timing"
              :options="TIMING_OPTIONS"
              option-label="label"
              option-value="value"
              :allow-empty="false"
            />
            <div v-if="timing === 'future'" class="flex flex-col gap-1 sm:w-1/2">
              <label class="text-sm font-medium">Effective date</label>
              <DatePicker
                v-model="effectiveDate"
                show-icon
                fluid
                date-format="yy-mm-dd"
                :min-date="tomorrow"
                placeholder="Select a date"
                class="w-full"
              />
            </div>
          </div>

          <div class="flex flex-col gap-3 border-t border-[var(--secondary-border-color)] pt-4">
            <h4 class="text-xs font-bold uppercase tracking-wide text-gray-600">
              Operation
            </h4>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium">Operation</label>
                <Select
                  v-model="operation"
                  :options="operationOptions"
                  option-label="label"
                  option-value="value"
                  class="w-full"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium">
                  {{ operation === 'multiply' ? 'Factor' : 'Amount' }}
                </label>
                <InputText
                  v-if="operation === 'multiply'"
                  v-model="factorText"
                  inputmode="decimal"
                  placeholder="e.g. .3"
                  class="w-full"
                />
                <InputNumber
                  v-else
                  v-model="operand"
                  mode="currency"
                  currency="USD"
                  fluid
                  :max-fraction-digits="2"
                  class="w-full"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium">Rounding increment</label>
                <Select
                  v-model="roundingChoice"
                  :options="ROUNDING_OPTIONS"
                  option-label="label"
                  option-value="value"
                  class="w-full"
                />
              </div>
              <div v-if="roundingChoice !== 'none'" class="flex flex-col gap-1">
                <label class="text-sm font-medium">Round to</label>
                <SelectButton
                  v-model="roundingDirection"
                  :options="ROUNDING_DIRECTION_OPTIONS"
                  option-label="label"
                  option-value="value"
                  :allow-empty="false"
                />
              </div>
              <div v-if="roundingChoice === 'custom'" class="flex flex-col gap-1">
                <label class="text-sm font-medium">Custom rounding increment</label>
                <InputNumber
                  v-model="customRounding"
                  mode="currency"
                  currency="USD"
                  fluid
                  :min="0.01"
                  :max-fraction-digits="2"
                  class="w-full"
                />
              </div>
            </div>
          </div>

          <div v-if="isPreviewLoading" class="flex items-center justify-center py-6">
            <ProgressSpinner style="width: 2.5rem; height: 2.5rem" :stroke-width="4" />
          </div>

          <div v-else-if="hasPreviewed" class="flex flex-col gap-2">
            <Message v-if="totalMatched === 0" severity="warn" :closable="false">
              No rows match this selection.
            </Message>
            <template v-else>
              <p class="text-sm text-[var(--black-64)]">
                Showing a sample of {{ sampleSize }} of {{ totalMatched }} matching rows, ordered by inventory number.
              </p>
              <p v-if="basis === 'source' && sourceField" class="text-sm text-[var(--black-64)]">
                "New" is calculated from each row's {{ sourceContextLabel }} {{ sourceField.label }} — "Current" is shown
                for reference only.
              </p>
              <DataTable
                :value="previewRows"
                scrollable
                scroll-height="16rem"
                table-style="min-width: 30rem;"
              >
                <Column field="inv_number" header="Inventory" />
                <Column header="Current">
                  <template #body="{ data }">
                    {{ formatAmount((data as PreviewRow).currentAmount) }}
                  </template>
                </Column>
                <Column header="New">
                  <template #body="{ data }">
                    <span v-if="(data as PreviewRow).skip" class="italic text-[var(--black-64)]">
                      Skipped — {{ (data as PreviewRow).skipReason }}
                    </span>
                    <span v-else>{{ formatAmount((data as PreviewRow).newAmount) }}</span>
                  </template>
                </Column>
              </DataTable>
            </template>
          </div>

          <div class="flex justify-end gap-2 border-t border-[var(--secondary-border-color)] pt-4">
            <Button severity="secondary" @click="resetAndClose">
              Cancel
            </Button>
            <Button
              :disabled="!hasPreviewed || !totalMatched || isApplyLoading"
              :loading="isApplyLoading"
              @click="confirmApply"
            >
              Apply
            </Button>
          </div>
        </div>

        <div v-else class="flex flex-col items-center justify-center gap-4 py-10">
          <ProgressSpinner v-if="isProcessing" style="width: 3rem; height: 3rem" :stroke-width="4" />
          <p v-if="isProcessing" class="text-sm text-[var(--black-64)]">
            Processing<span v-if="applyProgress"> — {{ applyProgress.affectedCount }} updated, {{ applyProgress.skippedCount }} skipped</span>…
          </p>
          <Message v-if="applyState === 'failed'" severity="error" :closable="false">
            {{ applyError }}
          </Message>
          <Button v-if="applyState === 'failed' || applyState === 'completed'" @click="resetAndClose">
            Close
          </Button>
        </div>
      </div>
    </template>
  </Modal>
  <ConfirmDialog group="bulk-edit-apply" />
</template>
